import { userIsLoggedIn } from '../user-session/user-session-use-cases/userIsLoggedIn'
import {
  codeFileGateway,
  CODE_FILES_BATCH_SIZE,
} from './code-file-gateways'
import { codeFileUseCases } from './code-file-use-cases'
import { userFileUseCases } from '../user-files/user-file-use-cases'
import { appDefaults } from '../../BotSimDefaults'
import { setSharedFileInRepo } from './code-file-store'
import { presentSharedFileTabs } from './code-file-presenter'
import { codeFileLocalStorageController, getUserFilesLocalStorageController } from '../../databaseworker/database.worker.controller'

class CodeFileController {
  async handleUserLoggedOut() {
    await Promise.all([
      codeFileLocalStorageController.deleteDatabase(),
      getUserFilesLocalStorageController('').deleteDatabase(),
    ])
  }

  async initializeUserFileTree() {
    const [userFileTree, localFileTree] = await Promise.all([
      codeFileGateway.getFileTreeFromCloud(),
      codeFileLocalStorageController.readMetadata().catch(() => undefined),
    ])
    const staleFileIds = await this.findStaleFileIds(userFileTree, localFileTree)
    await codeFileLocalStorageController.saveMetadata(userFileTree)

    const batches = []
    for (let i = 0; i < staleFileIds.length; i += CODE_FILES_BATCH_SIZE) {
      batches.push(staleFileIds.slice(i, i + CODE_FILES_BATCH_SIZE))
    }
    await Promise.all(batches.map(async (batch) => {
      const files = await codeFileGateway.getManyFromCloud(batch)
      await Promise.all(Object.entries(files)
        .filter(([, content]) => content !== null)
        .map(([uid, content]) => codeFileLocalStorageController.saveFile(uid, content)))
    }))

    return userFileTree
  }

  // A file needs downloading unless the local copy exists and has the same modifiedAt as the cloud,
  // since every save of a file's contents also updates its modifiedAt.
  async findStaleFileIds(userFileTree, localFileTree) {
    const fileIds = [...userFileTree?.tree?.keys()]
      // If an entry does not contain a 'tree' array, it is a file
      .filter(uid => !Array.isArray(userFileTree.tree.get(uid).tree))

    const isStale = await Promise.all(fileIds.map(async (uid) => {
      const localMetadata = localFileTree?.tree?.get(uid)
      if (!localMetadata || JSON.stringify(localMetadata.modifiedAt) !== JSON.stringify(userFileTree.tree.get(uid).modifiedAt)) {
        return true
      }
      return (await codeFileLocalStorageController.readFile(uid)) === undefined
    }))
    return fileIds.filter((uid, i) => isStale[i])
  }

  async resetFiles() {
    if (!userIsLoggedIn()) {
      await codeFileUseCases.saveFile(null, appDefaults.initialCodeSnippet.python)
    }

    return await userFileUseCases.saveUserProgress(appDefaults.userProgress)
  }

  async getSharedFile(fileId) {
    const metaData = await codeFileGateway.getMetaDataFromCloud(fileId)
    if (!metaData) {
      return
    }

    const fileContents = await codeFileGateway.getFromCloud(fileId, false, false)
    if (!fileContents) {
      return
    }

    setSharedFileInRepo({ ...metaData, content: fileContents, loadTs: Date.now() })
    presentSharedFileTabs()
    return metaData
  }
}

const codeFileController = new CodeFileController()
export {
  codeFileController,
}